export type FinishTiming = 'in_time' | 'overtime';

const parseTime = (value: string | Date | null | undefined): number | null => {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
};

/**
 * Whether a finished booking was returned within its booked time frame or in
 * overtime.
 *
 * Returns `null` — i.e. render nothing rather than fabricate timing info —
 * when the booking isn't finished, when there is no real finish timestamp
 * (still running, or a use case whose "finished" is derived from time alone,
 * e.g. apartment/venue without a rental row), or when the booked end is
 * unknown.
 *
 * Shared by the all-tenant bookings list (Master Mode) and the per-tenant
 * bookings table so the two can't drift apart — they showed different
 * information for the same booking before.
 */
export const getFinishTiming = (
    status: string | null | undefined,
    finishedAtDate: string | Date | null | undefined,
    endDate: string | Date | null | undefined,
): FinishTiming | null => {
    if ((status ?? '').toLowerCase() !== 'finished') return null;
    const finishedAt = parseTime(finishedAtDate);
    const bookedEnd = parseTime(endDate);
    if (finishedAt === null || bookedEnd === null) return null;
    return finishedAt <= bookedEnd ? 'in_time' : 'overtime';
};

/** i18n key for a finish timing, shared by both bookings surfaces. */
export const finishTimingLabelKey = (timing: FinishTiming): string =>
    `booking_details.finish_timing.${timing}`;

/**
 * Whether a booking is *in overtime* — the operator's question, which covers
 * two states the list otherwise keeps apart:
 *
 *  - finished late: returned after its booked end (`getFinishTiming` above);
 *  - still running late: handed out, booked end passed, not returned yet.
 *
 * The second one is the one that costs capacity right now — it is the board
 * that is physically gone while the guard still counts it as sellable. Only
 * `active` qualifies for it: a `booked` slot whose end passed was never picked
 * up, so nothing is out.
 *
 * Deliberately NOT the same predicate as the backend's capacity reduction
 * (`availability/capacityReduction.ts` + the overdue query in `groupService`).
 * That one subtracts a board from what we *sell*, so it waits out the tenant
 * grace period and requires a live holder; this one answers "has this booking
 * run past its end?" for a human reading the list, where the grace period
 * would hide exactly the rows they are looking for. Two questions, two
 * answers — kept apart on purpose rather than by accident.
 */
export const isOvertimeBooking = (
    status: string | null | undefined,
    finishedAtDate: string | Date | null | undefined,
    endDate: string | Date | null | undefined,
    now: number = Date.now(),
): boolean => {
    if (getFinishTiming(status, finishedAtDate, endDate) === 'overtime') return true;

    // BOTH values mean "running": the backend rewrites `active` to `overtime`
    // once a sup/rental booking passes its end (`orderAdminHandler.ts`), which is
    // precisely the state this function exists to find. Testing only for `active`
    // made the filter reject every booking it was built for (#2417 F1).
    const running = (status ?? '').toLowerCase();
    if (running !== 'active' && running !== 'overtime') return false;
    const bookedEnd = parseTime(endDate);
    return bookedEnd !== null && bookedEnd < now;
};
